import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Container from "@/components/Container";
import { api } from "@/lib/api";
import { ADMIN } from "@/constants/testIds";
import { toast } from "sonner";
import { Wrench } from "lucide-react";

const FIELDS = [
  ["expected_resale_value","Expected resale value"],
  ["purchase_price","Purchase price"],
  ["renovation_budget","Renovation budget"],
  ["stamp_duty","Stamp duty"],
  ["legal_fees","Legal fees"],
  ["survey_fees","Survey fees"],
  ["finance_cost","Finance cost"],
  ["holding_cost","Holding cost"],
  ["insurance","Insurance"],
  ["utilities","Utilities"],
  ["council_tax","Council tax"],
  ["agent_resale_fee","Agent resale fee"],
  ["marketing_staging_cost","Marketing / staging"],
  ["contingency","Contingency"],
  ["required_profit","Required profit"],
  ["required_roi_percentage","Required ROI (%)"],
  ["vat_tax","VAT / tax"],
  ["other_costs","Other costs"],
];

const fmt = (n) => "£" + (Number(n) || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 });

export default function Calculator() {
  const [searchParams] = useSearchParams();
  const propertyParam = searchParams.get("property") || "";
  const [form, setForm] = useState({ required_roi_percentage: 20, property_id: propertyParam });
  const [result, setResult] = useState(null);
  const [properties, setProperties] = useState([]);
  const [refurbHint, setRefurbHint] = useState(null);

  useEffect(() => {
    api.get("/properties?limit=500").then(r => setProperties(r.data?.rows || []));
  }, []);

  // Whenever the selected property changes, hydrate refurb estimate + asking price into the form.
  useEffect(() => {
    if (!form.property_id) { setRefurbHint(null); return; }
    api.get(`/properties/${form.property_id}`).then(r => {
      const p = r.data || {};
      const refurb = Number(p.estimated_renovation_cost) || 0;
      const purchase = Number(p.estimated_purchase_price) || Number(p.asking_price) || 0;
      setForm(f => ({
        ...f,
        renovation_budget: refurb > 0 ? refurb : (f.renovation_budget || ""),
        purchase_price: purchase > 0 ? purchase : (f.purchase_price || ""),
      }));
      setRefurbHint(refurb > 0 ? {
        total: refurb,
        items: (p.selected_refurb_items || []).length,
      } : null);
    }).catch(() => setRefurbHint(null));
  }, [form.property_id]);

  // Sync URL ?property=
  useEffect(() => {
    if (propertyParam) setForm(f => ({ ...f, property_id: propertyParam }));
  }, [propertyParam]);

  const compute = async () => {
    const payload = Object.fromEntries(Object.entries(form).map(([k,v]) => [k, Number(v) || 0]));
    payload.property_id = form.property_id || null;
    const { data } = await api.post("/calculator/compute", payload);
    setResult(data);
  };
  const save = async () => {
    if (!result) await compute();
    const payload = Object.fromEntries(Object.entries(form).map(([k,v]) => [k, Number(v) || 0]));
    payload.property_id = form.property_id || null;
    await api.post("/calculator/save", payload);
    toast.success("Calculator saved");
  };

  return (
    <Container className="py-8 md:py-12">
      <p className="overline">Deals</p>
      <h1 className="mt-2 text-4xl md:text-5xl font-serif text-ivory">Offer Calculator</h1>
      <p className="text-ivory/50 mt-2 text-sm">Max purchase price · ROI · net profit. Refurb estimates flow in from the property&apos;s Refurb Estimator.</p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 luxury-card p-6">
          <label className="block mb-4">
            <span className="overline">Property (optional)</span>
            <select value={form.property_id || ""} onChange={e => setForm({...form, property_id: e.target.value})}
              data-testid="calc-property-select"
              className="mt-2 w-full bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm">
              <option value="">— None —</option>
              {properties.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
          </label>

          {refurbHint && (
            <div className="luxury-card-ruby p-4 mb-5 flex items-center justify-between gap-3" data-testid="refurb-hint">
              <div className="flex items-center gap-3">
                <Wrench size={16} className="text-gold" />
                <p className="text-sm text-ivory/80">
                  Loaded <span className="text-gold font-mono">{fmt(refurbHint.total)}</span> from {refurbHint.items} refurb item{refurbHint.items === 1 ? "" : "s"} on this property.
                </p>
              </div>
              <span className="text-[0.6rem] uppercase tracking-[0.18em] text-ivory/40">auto-filled</span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {FIELDS.map(([k, label]) => (
              <label key={k} className="block">
                <span className="overline">{label}{refurbHint && k === "renovation_budget" ? <span className="text-gold normal-case ml-1">· from refurb</span> : null}</span>
                <input type="number" value={form[k] || ""} onChange={e => setForm({...form, [k]: e.target.value})}
                  data-testid={`calc-${k}`}
                  className="mt-2 w-full bg-charcoal-800 border border-charcoal-600 px-3 py-2 text-sm text-ivory focus:border-gold focus:outline-none" />
              </label>
            ))}
          </div>
          <div className="mt-6 flex gap-3">
            <button onClick={compute} className="btn-ruby" data-testid={ADMIN.computeCalc}>Compute</button>
            <button onClick={save} className="btn-outline" data-testid={ADMIN.saveCalc}>Save to Property</button>
          </div>
        </div>

        <div className="luxury-card p-6 h-fit" data-testid="calc-result">
          <p className="overline">Result</p>
          {!result ? <p className="text-ivory/40 text-sm mt-4">Run compute to see results.</p> : (
            <dl className="mt-5 space-y-3 text-sm">
              <Row k="Maximum purchase price" v={fmt(result.max_purchase_price)} accent />
              <Row k="Estimated total cost" v={fmt(result.estimated_total_cost)} />
              <Row k="Gross profit" v={fmt(result.estimated_gross_profit)} />
              <Row k="Net profit (after target)" v={fmt(result.estimated_net_profit)} />
              <Row k="ROI" v={`${result.roi_percentage}%`} accent />
              <Row k="Margin" v={`${result.margin_percentage}%`} />
              {result.profit_warning && <p className="text-ruby-light text-xs mt-3">⚠ Profit below target</p>}
              {result.risk_warning && <p className="text-bronze text-xs">⚠ Margin under 15%</p>}
            </dl>
          )}
        </div>
      </div>
    </Container>
  );
}

function Row({ k, v, accent }) {
  return <div className="flex justify-between border-b border-charcoal-600/60 pb-2"><dt className="text-ivory/50 uppercase tracking-[0.15em] text-[0.65rem]">{k}</dt><dd className={accent ? "text-gold" : "text-ivory"}>{v}</dd></div>;
}
